import { DomainRedux } from "@/Shared/Types";
import { PollerActions } from ".";

const POLLER_REGISTRY = new Map<DomainRedux, PollerActions>();

/**
 * Completely removes the poller from the registry.
 * Warning: This will delete all references to the poller.
 * The poller will no longer be accessible.
 * To simply stop the poller without removing it, use stopPoller().
 */
export function removePoller(key : DomainRedux) {
    POLLER_REGISTRY.delete(key);
}

/**
 * Adds or updates a poller in the registry with the specified key.
 * @param key - Unique identifier for the poller.
 * @param poller - The poller instance to store.
 */
export function setPoller(key : DomainRedux, poller: PollerActions) {
    POLLER_REGISTRY.set(key, poller);
}

/**
 * Retrieves the poller associated with the given key.
 * @param key - Unique identifier of the poller to retrieve.
 * @returns The poller instance or undefined if not found.
 */
export function getPoller(key : DomainRedux) {
    return POLLER_REGISTRY.get(key);
}

/**
 * Starts the poller associated with the given key.
 * Does nothing if the poller is already running or doesn't exist.
 * @param key - Unique identifier of the poller to start.
 */
export function startPoller(key : DomainRedux) {
    POLLER_REGISTRY.get(key)?.start();
}

/**
 * Stops the poller associated with the given key.
 * Does nothing if the poller is already stopped or doesn't exist.
 * @param key - Unique identifier of the poller to stop.
 */
export function stopPoller(key : DomainRedux) {
    POLLER_REGISTRY.get(key)?.stop();
}

/**
 * Restarts the poller associated with the given key.
 * This will stop and then start the poller.
 * @param key - Unique identifier of the poller to restart.
 */
export function restartPoller(key : DomainRedux) {
    POLLER_REGISTRY.get(key)?.restart();
}

/**
 * Checks if a poller with the given key exists in the registry.
 * @param key - Unique identifier of the poller.
 * @returns True if the poller exists, false otherwise.
 */
export function hasPoller(key : DomainRedux): boolean {
    return POLLER_REGISTRY.has(key);
}

/**
 * Stops and removes all pollers from the registry.
 * Use with caution, as this clears the entire poller state.
 */
export function disposeAllPollers() {
    for (const poller of POLLER_REGISTRY.values()) {
        poller.stop();
    }
    POLLER_REGISTRY.clear();
}


/**
 * Returns a new Map containing all pollers currently registered.
 * This provides a snapshot of the poller registry at the time of the call.
 * Modifying the returned Map does NOT affect the internal registry.
 * 
 * @returns A new Map with all poller keys and their corresponding PollerActions.
 */
export function getAllPollers(): Map<DomainRedux, PollerActions> {
  return new Map(POLLER_REGISTRY);
}